/**
 * CircleCIConnectModal — paste-token (api-key) form for the CircleCI connector.
 *
 * CircleCI auth is an API KEY (a personal API token), not OAuth — CircleCI has
 * no consumer OAuth. The user pastes a SINGLE value:
 *   - API token (CircleCI → User Settings → Personal API Tokens;
 *                https://app.circleci.com/settings/user/tokens)
 *
 * Backend validates (GET /api/v2/me with the Circle-Token header) before
 * persisting — structured error surfaces on err.message. Mirrors
 * LinearConnectModal for the shared shape; just one field.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectCircleCI } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function CircleCIConnectModal({ isOpen, onClose, onConnected }) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectCircleCI();

  useEffect(() => {
    if (isOpen) {
      setApiKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!apiKey.trim()) {
      setError('API token is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { apiKey: apiKey.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect CircleCI. Please check your API token.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect CircleCI"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect CircleCI</h2>
        <p className="lark-modal-description">
          Create a personal API token at{' '}
          <a
            href="https://app.circleci.com/settings/user/tokens"
            target="_blank"
            rel="noopener noreferrer"
          >
            CircleCI → User Settings → Personal API Tokens
          </a>
          , then paste it below. The flaky-test-fixer agent uses it to read your
          project&apos;s flaky tests from the CircleCI Insights API.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="circleci-api-key">API Token</label>
          <input
            id="circleci-api-key"
            type="password"
            value={apiKey}
            placeholder="CCIPAT_..."
            onChange={(e) => { setApiKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !apiKey.trim()}
            aria-label="Submit CircleCI credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel CircleCI connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
